import React from "react";
import { useT } from "@transifex/react";
import { useHistory } from "react-router-dom";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { SummaryModes } from "./Summary";
import CtasWrapper, { ButtonGroup } from "../../../wrapper/create/ctasWrapper/CtasWrapper";
import StyledButtonWithIcon from "../../../basic/styledButtonWithIcon/StyledButtonWithIcon";
import StyledButton from "../../../basic/styledButton/StyledButton";
import ResponsiveButton from "../../../basic/responsiveButton/ResponsiveButton";
import { useLogger } from "../../../hooks/useLogger";
import { PreviewView } from "../../../enums/views";

import { ButtonWrapper, StyledButtonWithoutIcon, FinishTestButton } from "./StyledComponents";

interface SummaryFooterProps {
    currentMode: SummaryModes;
    isSaveExerciseDisabled: boolean;
    isTestBeingSynced: boolean;
    onClickButtonBack: () => void;
    onClickButtonContinue: () => void;
}

const SummaryFooter: React.FC<SummaryFooterProps> = (props) => {
    const { testContent, currentTestId } = useTestCreationContext();
    const { logEvent } = useLogger();

    const history = useHistory();
    const t = useT();

    const t_saveTest = t("Complete test creation", { _tags: "CreateTest,Summary" });
    const t_saving = t("Saving...", { _tags: "CreateTest,Summary" });
    const t_saveExercise = t("Save exercise", { _tags: "CreateTest,Summary" });
    const t_preview = t("Preview", { _tags: "CreateTest,Summary" });
    const t_back = t("Back", { _tags: "CreateTest,Summary" });

    const isOverviewMode = props.currentMode === SummaryModes.OVERVIEW;
    const isSelectExerciseTypeMode = props.currentMode === SummaryModes.SELECT_EXERCISE_TYPE;
    const isExerciseMode = props.currentMode === SummaryModes.EXERCISE_DETAILS;

    const canSaveExercise = !props.isSaveExerciseDisabled && !props.isTestBeingSynced;

    function openPreview() {
        try {
            logEvent("Opened preview", {
                testId: currentTestId,
                origin: "test creation",
            });
            // current test data already present in test creation context
            history.push(`/create/preview?mode=${PreviewView.MOBILE}`, {
                urlBeforePreview: "/create/summary",
            });
        } catch (e) {
            alert(e);
        }
    }

    return (
        <CtasWrapper
            showScrollToTop
            isSticky
        >
            <ButtonWrapper>
                <ResponsiveButton
                    onClick={props.onClickButtonBack}
                    iconName="chevron-left"
                    label={t_back}
                >
                    <StyledButton
                        onClick={props.onClickButtonBack}
                        arrowDirection={"LEFT"}
                        buttonStyle={"BLANK"}
                    >
                        {t_back}
                    </StyledButton>
                </ResponsiveButton>
            </ButtonWrapper>
            <ButtonGroup>
                {(isOverviewMode || isSelectExerciseTypeMode) && (
                    <ButtonWrapper>
                        <StyledButtonWithIcon
                            data-testid="button-preview"
                            icon={"preview"}
                            iconPosition={"LEFT"}
                            onClick={openPreview}
                            arrowDirection={"NONE"}
                            buttonStyle={"ORANGE_BLANK"}
                            disabled={testContent.length < 1}
                        >
                            {t_preview}
                        </StyledButtonWithIcon>
                    </ButtonWrapper>
                )}
                {(isOverviewMode || isSelectExerciseTypeMode) && (
                    <FinishTestButton
                        data-testid="button-finish-test"
                        onClick={props.onClickButtonContinue}
                        icon={"exercise-ok"}
                        iconPosition={"LEFT"}
                        arrowDirection={"NONE"}
                        buttonStyle={"PRIMARY"}
                        disabled={testContent.length < 1 || props.isTestBeingSynced}
                    >
                        {props.isTestBeingSynced ? t_saving : t_saveTest}
                    </FinishTestButton>
                )}
                {isExerciseMode && (
                    <StyledButtonWithoutIcon
                        data-testid="button-save-exercise"
                        arrowDirection="NONE"
                        onClick={props.onClickButtonContinue}
                        disabled={!canSaveExercise}
                    >
                        {t_saveExercise}
                    </StyledButtonWithoutIcon>
                )}
            </ButtonGroup>
        </CtasWrapper>
    );
};

export default SummaryFooter;
